import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { supabase } from "../../supabaseClient";

import formatRelativeTime from "./formatRelativeTime";

import "./CommentThread.css";

/**
 * ============================================================
 * HOBBLI SOCIAL COMMENT THREAD
 * ============================================================
 *
 * Handles:
 *
 * - Loading comments for a target
 * - User enrichment
 * - Nested replies
 * - Posting comments/replies
 * - Soft deleting comments
 * - Realtime refresh
 * - Focused-comment deep links
 * - Shared relative timestamps
 * - Parent social-summary refresh callbacks
 *
 * ============================================================
 */

function getDisplayName(user) {
  return user?.display_name || "collector";
}

export default function CommentThread({
  targetType = "user_item",
  targetId,
  currentUser,
  focusCommentId = null,
  onChange,
}) {
  const [comments, setComments] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [submitting, setSubmitting] =
    useState(false);

  const [text, setText] =
    useState("");

  const [replyTo, setReplyTo] =
    useState(null);

  const [error, setError] =
    useState("");

  /**
   * ============================================================
   * LOAD COMMENTS
   * ============================================================
   */

  const loadComments =
    useCallback(
      async () => {
        if (!targetId) {
          setComments([]);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError("");

        try {
          const {
            data: rows,
            error: commentsError,
          } = await supabase
            .from(
              "social_comments"
            )
            .select(
              [
                "id",
                "user_id",
                "target_type",
                "target_id",
                "parent_comment_id",
                "comment",
                "created_at",
                "updated_at",
                "deleted_at",
              ].join(",")
            )
            .eq(
              "target_type",
              targetType
            )
            .eq(
              "target_id",
              targetId
            )
            .is(
              "deleted_at",
              null
            )
            .order(
              "created_at",
              {
                ascending: true,
              }
            );

          if (commentsError) {
            throw commentsError;
          }

          const userIds = [
            ...new Set(
              (
                rows ||
                []
              )
                .map(
                  (row) =>
                    row.user_id
                )
                .filter(
                  Boolean
                )
            ),
          ];

          let usersById = {};

          if (
            userIds.length
          ) {
            const {
              data: users,
              error: usersError,
            } = await supabase.rpc("get_user_profiles", {
              p_user_ids: userIds,
            });

            if (
              usersError
            ) {
              console.error(
                "Failed to load comment authors:",
                usersError
              );
            } else {
              usersById =
                Object.fromEntries(
                  (
                    users ||
                    []
                  ).map(
                    (
                      user
                    ) => [
                      user.id,
                      user,
                    ]
                  )
                );
            }
          }

          setComments(
            (
              rows ||
              []
            ).map(
              (
                row
              ) => ({
                ...row,

                author:
                  usersById[
                    row.user_id
                  ] ||
                  null,
              })
            )
          );
        } catch (
          err
        ) {
          console.error(
            "Failed to load comments:",
            err
          );

          setError(
            "Comments could not be loaded."
          );
        } finally {
          setLoading(
            false
          );
        }
      },
      [
        targetId,
        targetType,
      ]
    );

  /**
   * Initial load.
   */

  useEffect(() => {
    loadComments();
  }, [
    loadComments,
  ]);

  /**
   * ============================================================
   * REALTIME COMMENTS
   * ============================================================
   *
   * Keeps the open thread synchronized if another user posts,
   * replies, edits, or deletes from another browser/session.
   * ============================================================
   */

  useEffect(() => {
    if (
      !targetId
    ) {
      return undefined;
    }

    const channel =
      supabase
        .channel(
          `social-comments-${targetType}-${targetId}`
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema:
              "public",
            table:
              "social_comments",

            filter:
              `target_id=eq.${targetId}`,
          },
          (
            payload
          ) => {
            const row =
              payload?.new &&
              Object.keys(
                payload.new
              ).length
                ? payload.new
                : payload?.old;

            /**
             * target_id is the realtime filter.
             * Verify target_type too in case IDs overlap across
             * future polymorphic social targets.
             */
            if (
              !row
                ?.target_type ||
              row.target_type ===
                targetType
            ) {
              loadComments();
            }
          }
        )
        .subscribe();

    return () => {
      supabase.removeChannel(
        channel
      );
    };
  }, [
    targetId,
    targetType,
    loadComments,
  ]);

  /**
   * ============================================================
   * FOCUSED COMMENT
   * ============================================================
   *
   * Used for notification URLs such as:
   *
   * /hub?item=1080&comment=<uuid>
   * ============================================================
   */

  useEffect(() => {
    if (
      !focusCommentId ||
      loading
    ) {
      return undefined;
    }

    const timer =
      window.setTimeout(
        () => {
          const element =
            document.getElementById(
              `comment-${focusCommentId}`
            );

          if (
            !element
          ) {
            return;
          }

          element.scrollIntoView({
            behavior:
              "smooth",
            block:
              "center",
          });

          element.classList.add(
            "hub-comment-focus"
          );

          window.setTimeout(
            () => {
              element.classList.remove(
                "hub-comment-focus"
              );
            },
            2200
          );
        },
        120
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    focusCommentId,
    loading,
    comments,
  ]);

  /**
   * ============================================================
   * COMMENT TREE
   * ============================================================
   */

  const childrenByParent =
    useMemo(
      () => {
        const map =
          new Map();

        comments.forEach(
          (
            comment
          ) => {
            const key =
              comment.parent_comment_id ||
              "root";

            if (
              !map.has(
                key
              )
            ) {
              map.set(
                key,
                []
              );
            }

            map
              .get(
                key
              )
              .push(
                comment
              );
          }
        );

        return map;
      },
      [
        comments,
      ]
    );

  /**
   * ============================================================
   * POST COMMENT / REPLY
   * ============================================================
   */

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      const clean =
        text.trim();

      if (
        !currentUser?.id ||
        !clean ||
        !targetId ||
        submitting
      ) {
        return;
      }

      setSubmitting(
        true
      );

      setError("");

      try {
        const {
          data:
            insertedRows,
          error:
            insertError,
        } =
          await supabase
            .from(
              "social_comments"
            )
            .insert({
              user_id:
                currentUser.id,

              target_type:
                targetType,

              /**
               * social_comments.target_id is polymorphic TEXT.
               */
              target_id:
                String(
                  targetId
                ),

              parent_comment_id:
                replyTo?.id ||
                null,

              comment:
                clean,
            })
            .select(
              "id, parent_comment_id"
            );

        if (
          insertError
        ) {
          throw insertError;
        }

        setText("");
        setReplyTo(null);

        /**
         * Refresh this thread immediately.
         */
        await loadComments();

        /**
         * Tell HobbliHub's batched social-summary layer that this
         * item's social data changed.
         *
         * This makes:
         *
         *   💬 count
         *   latest comment preview
         *
         * update immediately without waiting for realtime.
         */
        onChange?.({
          action:
            replyTo
              ? "reply_added"
              : "comment_added",

          targetType,
          targetId,

          commentId:
            insertedRows?.[0]
              ?.id ||
            null,
        });
      } catch (
        err
      ) {
        console.error(
          "Failed to post comment:",
          err
        );

        setError(
          err?.message ||
            "Your comment could not be posted."
        );
      } finally {
        setSubmitting(
          false
        );
      }
    };

  /**
   * ============================================================
   * DELETE COMMENT
   * ============================================================
   *
   * Soft delete only.
   * ============================================================
   */

  const handleDelete =
    async (
      comment
    ) => {
      if (
        !currentUser?.id ||
        comment.user_id !==
          currentUser.id
      ) {
        return;
      }

      try {
        const now =
          new Date()
            .toISOString();

        const {
          error:
            deleteError,
        } =
          await supabase
            .from(
              "social_comments"
            )
            .update({
              deleted_at: new Date().toISOString(),
            })
            .eq(
              "id",
              comment.id
            )
            .eq(
              "user_id",
              currentUser.id
            );

        if (
          deleteError
        ) {
          throw deleteError;
        }

        /**
         * If the deleted comment was being replied to,
         * clear the reply composer.
         */
        if (
          replyTo?.id ===
          comment.id
        ) {
          setReplyTo(
            null
          );
        }

        await loadComments();

        /**
         * Refresh public-card social summary immediately.
         */
        onChange?.({
          action:
            "comment_removed",

          targetType,
          targetId,

          commentId:
            comment.id,
        });
      } catch (
        err
      ) {
        console.error(
          "Failed to delete comment:",
          err
        );

        setError(
          "The comment could not be deleted."
        );
      }
    };

  /**
   * ============================================================
   * RENDER COMMENT
   * ============================================================
   */

  const renderComment =
    (
      comment,
      depth = 0
    ) => {
      const children =
        childrenByParent.get(
          comment.id
        ) ||
        [];

      return (
        <div
          key={
            comment.id
          }
          className={
            depth
              ? "hub-comment-reply-wrap"
              : ""
          }
        >
          <article
            id={`comment-${comment.id}`}
            className={`hub-comment ${
              focusCommentId ===
              comment.id
                ? "hub-comment-target"
                : ""
            }`}
          >
            <div className="hub-comment-header">
              <strong>
                @
                {getDisplayName(
                  comment.author
                )}
              </strong>

              <span>
                {formatRelativeTime(
                  comment.created_at
                )}
              </span>
            </div>

            <p>
              {
                comment.comment
              }
            </p>

            <div className="hub-comment-actions">
              {currentUser?.id && (
                <button
                  type="button"
                  onClick={() =>
                    setReplyTo(
                      comment
                    )
                  }
                >
                  Reply
                </button>
              )}

              {currentUser?.id ===
                comment.user_id && (
                <button
                  type="button"
                  onClick={() =>
                    handleDelete(
                      comment
                    )
                  }
                >
                  Delete
                </button>
              )}
            </div>
          </article>

          {children.map(
            (
              child
            ) =>
              renderComment(
                child,
                depth +
                  1
              )
          )}
        </div>
      );
    };

  const rootComments =
    childrenByParent.get(
      "root"
    ) ||
    [];

  /**
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <section
      className="hub-comments"
      aria-label="Comments"
    >
      <div className="hub-comments-title-row">
        <h3>
          Comments
        </h3>

        <span>
          {
            comments.length
          }
        </span>
      </div>

      {currentUser?.id ? (
        <form
          className="hub-comment-form"
          onSubmit={
            handleSubmit
          }
        >
          {replyTo && (
            <div className="hub-comment-reply-banner">
              <span>
                Replying to @
                {getDisplayName(
                  replyTo.author
                )}
              </span>

              <button
                type="button"
                onClick={() =>
                  setReplyTo(
                    null
                  )
                }
              >
                Cancel
              </button>
            </div>
          )}

          <textarea
            value={
              text
            }
            onChange={(
              event
            ) =>
              setText(
                event.target.value
              )
            }
            placeholder={
              replyTo
                ? "Write a reply…"
                : "Add a comment…"
            }
            rows={
              3
            }
            maxLength={
              2000
            }
          />

          <div className="hub-comment-form-footer">
            <span>
              {text.length}
              /2000
            </span>

            <button
              type="submit"
              disabled={
                !text.trim() ||
                submitting
              }
            >
              {submitting
                ? "Posting…"
                : replyTo
                  ? "Reply"
                  : "Comment"}
            </button>
          </div>
        </form>
      ) : (
        <p className="hub-comment-empty">
          Sign in to join
          the conversation.
        </p>
      )}

      {error && (
        <p className="hub-comment-error">
          {
            error
          }
        </p>
      )}

      <div className="hub-comment-list">
        {loading ? (
          <p className="hub-comment-empty">
            Loading
            comments…
          </p>
        ) : rootComments.length ? (
          rootComments.map(
            (
              comment
            ) =>
              renderComment(
                comment
              )
          )
        ) : (
          <p className="hub-comment-empty">
            No comments yet.
            Start the
            conversation.
          </p>
        )}
      </div>
    </section>
  );
}